import Backbone from 'backbone';
import React from 'react';
import ReactDOM from 'react-dom/client';
import _ from 'underscore';

import * as analytics from 'analytics/Analytics';
import {ResidueType} from 'biojs/BioConstants';
import {ANALYTICS_MODULE, ACTION_CATEGORY} from 'constants/AnalyticsConstants';
import AddNewFeatureForm from 'molbio/feature-libraries/AddNewFeatureForm';
import {Feature as ComponentFeature, Model as FeatureLibrary} from 'molbio/feature-libraries/FeatureLibrary';
import {
  deleteFeaturesFromLibrary,
  moveFeaturesToLibrary,
  updateFeatures,
} from 'molbio/feature-libraries/FeatureLibraryActions';
import {Feature} from 'molbio/feature-libraries/FeatureLibraryConstants';
import FeatureTable from 'molbio/feature-libraries/FeatureTable';
import notifications from 'notifications';
import {renderToDOM, unmountRootsAsync} from 'redux-stores/ReduxDOMRender';

export interface LibraryViewOptions extends Backbone.ViewOptions<FeatureLibrary> {
  libraryMember: typeof ComponentFeature;
  collectionName: string;
}

class LibraryView extends Backbone.View<FeatureLibrary> {
  library!: FeatureLibrary;
  libraryMember!: typeof ComponentFeature;
  collectionName!: string;
  comparator!: string;
  reverse!: boolean;
  members!: Backbone.Collection<ComponentFeature>;
  reactRoot: ReactDOM.Root | null = null;
  dead: boolean = false;

  static initClass(): void {
    this.prototype.el = '.library-view';
  }

  override initialize(options: LibraryViewOptions): void {
    this.libraryMember = options.libraryMember;
    this.collectionName = options.collectionName;
    this.comparator = 'name';
    this.reverse = false;
  }

  die(): void {
    this.dead = true;
    this.stopListening();
    this.undelegateEvents();
    this.remove();
  }

  override remove(): this {
    unmountRootsAsync([this.reactRoot], () => {
      this.reactRoot = null;
    });
    return this;
  }

  /**
   * Creates a new feature in the library.
   * @param newFeature - The new feature to create.
   * @returns a promise that resolves to whether or not the feature was created successfully.
   */
  createFeature(newFeature: Partial<Feature>): Promise<boolean> {
    // eslint-disable-next-line new-cap
    const member = new this.libraryMember(newFeature);
    const invalid = member.validate(member.toJSON());
    if (invalid) {
      notifications.error(invalid);
      return Promise.resolve(false);
    }
    return new Promise((resolve, reject) => {
      member
        .save()
        .done(() => {
          this.library.get(this.collectionName).add(member);
          notifications.success('Item added to folder successfully.');
          analytics.track('feature_library', 'added_feature', {
            product: ANALYTICS_MODULE.MOLBIO,
            actionCategory: ACTION_CATEGORY.WRITE,
            featureLibraryId: this.library.get('api_identifier'),
            featureId: member.id,
            featureType: member.get('feature_type'),
            matchType: member.get('match_type'),
            name: member.get('name'),
          });
          resolve(true);
        })
        .fail((error: Response) => {
          notifications.notifyError(error);
          resolve(false);
        });
    });
  }

  update(library: FeatureLibrary): void {
    this.library = library;
    this.stopListening();
    this.members = this.library.get(this.collectionName);
    this.listenTo(this.members, 'add remove', this.render.bind(this));
  }

  override render(): LibraryView {
    if (this.dead) {
      return this;
    }

    const models = _.clone(this.members.models);
    if (this.reverse) {
      models.reverse();
    }

    const features = this.members.models.map((feature) => feature.attributes);
    const featureCollection = this.members;
    const allMembers = this.members.models;
    this.reactRoot = renderToDOM(
      <>
        {this.library.get('permissions').writable && (
          <AddNewFeatureForm
            onAddNewFeature={async (newFeature) =>
              await this.createFeature({
                name: newFeature.name,
                bases: newFeature.residues,
                color: newFeature.color,
                match_type: newFeature.residueType === ResidueType.NUCLEOTIDE ? 'nucleotide' : 'protein',
                feature_type: newFeature.featureType,
                library_id: this.library.id,
              })
            }
          />
        )}
        <FeatureTable
          isReadOnly={!this.library.get('permissions').writable}
          features={features}
          libraries={this.library.collection.models.map((library) => library.attributes)}
          currentLibraryId={this.library.get('api_identifier')}
          onUpdateFeature={(feature, field, value) => {
            updateFeatures([{id: feature.id, [field]: value}], this.library.id)
              .fail((res) => {
                notifications.notifyError(res);
              })
              .done(() => {
                this.library.fetch().done(this.render.bind(this));
                notifications.success(`1 feature was saved successfully.`);
                analytics.track('feature_library', 'edited_feature', {
                  product: ANALYTICS_MODULE.MOLBIO,
                  actionCategory: ACTION_CATEGORY.WRITE,
                  featureLibraryId: this.library.get('api_identifier'),
                  featureId: feature.id,
                });
              });
          }}
          onMoveToLibrary={(libraryId, selectedFeatures) => {
            const destinationLibrary = this.library.collection.models.find(
              (m) => m.get('api_identifier') === libraryId
            );
            moveFeaturesToLibrary(
              selectedFeatures.map((feature) => feature.id),
              this.library.id,
              destinationLibrary.id
            )
              .fail(notifications.notifyError)
              .done(() => {
                for (const feature of selectedFeatures) {
                  const member = allMembers.find((m) => m.get('id') === feature.id);
                  if (member) {
                    member.set({
                      library_id: destinationLibrary.id,
                      selected: false,
                    });
                  }
                  analytics.track('feature_library', 'moved_feature', {
                    product: ANALYTICS_MODULE.MOLBIO,
                    actionCategory: ACTION_CATEGORY.WRITE,
                    sourceFeatureLibraryId: this.library.get('api_identifier'),
                    destinationFeatureLibraryId: libraryId,
                    featureId: feature.id,
                  });
                }
                const membersToMove = allMembers.filter((member) =>
                  selectedFeatures.some((feature) => feature.id === member.get('id'))
                );
                featureCollection.remove(membersToMove);
                destinationLibrary.get('features').add(membersToMove);
                notifications.success(
                  <>
                    Successfully moved {selectedFeatures.length} items to{' '}
                    <strong>{destinationLibrary.get('name')}</strong>.
                  </>
                );
              });
          }}
          onDeleteFeatures={(selectedFeatures) => {
            deleteFeaturesFromLibrary(
              selectedFeatures.map((feature) => feature.id),
              this.library.get('id')
            )
              .fail(notifications.notifyError)
              .done(() => {
                const members = allMembers.filter((member) =>
                  selectedFeatures.some((feature) => feature.id === member.get('id'))
                );
                featureCollection.remove(members);
                notifications.success(`Successfully deleted ${selectedFeatures.length} item(s).`);
                selectedFeatures.forEach((feature) => {
                  analytics.track('feature_library', 'deleted_feature', {
                    product: ANALYTICS_MODULE.MOLBIO,
                    actionCategory: ACTION_CATEGORY.WRITE,
                    featureLibraryId: this.library.get('api_identifier'),
                    featureId: feature.id,
                  });
                });
              });
          }}
        />
      </>,
      this.el,
      this.reactRoot
    );
    return this;
  }
}
LibraryView.initClass();

export default LibraryView;
